// src/Views/Admin/FootballAdmin.js
// FIXED: Added sendViaEmail field for Cloud Function to send emails
// - Single match announcement: "Football Update"
// - Broadcast all picks: "Football Update"
// - Announce results: "Football Results"

import React, { useState, useEffect, useCallback } from 'react';
import { Icon } from '@iconify/react';
import './FootballAdmin.css';
import { db } from '../../firebase';
import { useAuth } from '../../context/AuthContext';
import {
    collection,
    addDoc,
    serverTimestamp,
    query,
    where,
    onSnapshot,
    updateDoc,
    doc,
    deleteDoc,
    getDoc,
    orderBy,
    limit
} from 'firebase/firestore';

const initialForm = {
    league: '',
    homeTeam: '',
    awayTeam: '',
    matchLabel: '',
    market: '',
    pick: '',
    odd: '',
    confidence: '',
    edge: '',
    kickoffLabel: '',
    stakeHint: '',
    reason: ''
};

const FootballAdmin = () => {
    const { user, loading: authLoading } = useAuth();
    const [isAdmin, setIsAdmin] = useState(false);
    const [checkingAdmin, setCheckingAdmin] = useState(true);
    const [activePlan, setActivePlan] = useState('freemium');
    const [dayKey, setDayKey] = useState('today');
    const [form, setForm] = useState(initialForm);
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    // 🔥 BROADCAST STATES - Default FALSE
    const [sendAnnouncement, setSendAnnouncement] = useState(false);
    const [broadcasting, setBroadcasting] = useState(false);

    // 1. Check Admin Privileges
    useEffect(() => {
        const verifyAdmin = async () => {
            if (!user) {
                setIsAdmin(false);
                setCheckingAdmin(false);
                return;
            }
            try {
                const userDoc = await getDoc(doc(db, 'users', user.uid));
                if (userDoc.exists() && userDoc.data().isAdmin === true) {
                    setIsAdmin(true);
                } else {
                    setIsAdmin(false);
                }
            } catch (err) {
                console.error('Security check error', err);
                setIsAdmin(false);
            } finally {
                setCheckingAdmin(false);
            }
        };
        if (!authLoading) {
            verifyAdmin();
        }
    }, [user, authLoading]);

    const getCollectionAndDayField = useCallback(() => {
        if (activePlan === 'freemium') {
            return { collectionName: 'football_fremium', dayField: 'dayKey' };
        }
        if (activePlan === 'premium') {
            return { collectionName: 'football_premium', dayField: 'day' };
        }
        return { collectionName: 'football_premium_hrhr', dayField: 'day' };
    }, [activePlan]);

    const dayLabel = (key) => {
        if (key === 'history') return 'History (Archived)';
        if (key === 'tomorrow') return 'Tomorrow';
        return 'Today';
    };

    // 2. Load Data
    useEffect(() => {
        if (!isAdmin) return;
        const { collectionName, dayField } = getCollectionAndDayField();
        const colRef = collection(db, collectionName);
        let q;
        if (dayKey === 'history') {
            q = query(
                colRef,
                where(dayField, '==', 'history'),
                orderBy('createdAt', 'desc'),
                limit(50)
            );
        } else {
            q = query(colRef, where(dayField, '==', dayKey));
        }
        setLoading(true);
        setError('');
        const unsub = onSnapshot(
            q,
            (snapshot) => {
                const docs = snapshot.docs.map((d) => {
                    const data = d.data();
                    if (activePlan === 'freemium') {
                        return {
                            id: d.id,
                            league: data.league || '',
                            match: data.match || '',
                            market: data.market || '',
                            confidence: typeof data.confidence === 'number' ? data.confidence : null,
                            edge: data.edge || '',
                            kickoffLabel: data.kickoffLabel || '',
                            status: data.status || 'pending'
                        };
                    }
                    return {
                        id: d.id,
                        league: data.league || '',
                        homeTeam: data.homeTeam || '',
                        awayTeam: data.awayTeam || '',
                        match: data.homeTeam && data.awayTeam
                            ? `${data.homeTeam} vs ${data.awayTeam}`
                            : (data.match || ''),
                        market: data.pick || data.market || '',
                        odd: typeof data.odd === 'number' ? data.odd : null,
                        confidence: typeof data.confidence === 'number' ? data.confidence : null,
                        edge: typeof data.edge === 'string'
                            ? data.edge
                            : (typeof data.odd === 'number' ? `@${data.odd}` : ''),
                        kickoffLabel: data.kickoffTimestamp || '',
                        stakeHint: data.stakeHint || '',
                        reason: data.reason || '',
                        status: data.status || 'pending'
                    };
                });
                setItems(docs);
                setLoading(false);
            },
            (err) => {
                console.error('Snapshot error:', err);
                if (!err.message.includes('index')) {
                    setError('Failed to load matches.');
                }
                setLoading(false);
            }
        );
        return () => unsub();
    }, [activePlan, dayKey, isAdmin, getCollectionAndDayField]);

    const handlePlanChange = (plan) => {
        setActivePlan(plan);
        setError('');
        setSendAnnouncement(false);
    };

    const handleDayChange = (day) => {
        setDayKey(day);
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm((prev) => ({ ...prev, [name]: value }));
    };

    // =========================================================
    // 🔥 SUBMIT - Announcement ONLY if explicitly checked
    // =========================================================
    const handleSubmit = async (e) => {
        e.preventDefault();
        const shouldAnnounce = sendAnnouncement === true;
        console.log('[FootballAdmin] === SAVE STARTED ===');
        console.log('[FootballAdmin] sendAnnouncement checkbox value:', shouldAnnounce);
        setSaving(true);
        setError('');
        try {
            const { collectionName, dayField } = getCollectionAndDayField();
            const colRef = collection(db, collectionName);
            let payload;
            let matchTitle = '';
            if (activePlan === 'freemium') {
                const matchText = form.matchLabel.trim() || (
                    form.homeTeam.trim() && form.awayTeam.trim()
                        ? `${form.homeTeam.trim()} v ${form.awayTeam.trim()}`
                        : ''
                );
                matchTitle = matchText || form.league.trim();
                payload = {
                    [dayField]: dayKey,
                    league: form.league.trim(),
                    match: matchText,
                    market: form.market.trim(),
                    confidence: form.confidence ? Number(form.confidence) : 0,
                    kickoffLabel: form.kickoffLabel.trim(),
                    edge: form.edge.trim() || '+0.0%',
                    status: 'pending',
                    createdAt: serverTimestamp()
                };
            } else {
                matchTitle = form.matchLabel.trim() || (
                    form.homeTeam.trim() && form.awayTeam.trim()
                        ? `${form.homeTeam.trim()} vs ${form.awayTeam.trim()}`
                        : form.league.trim()
                );
                payload = {
                    [dayField]: dayKey,
                    league: form.league.trim(),
                    homeTeam: form.homeTeam.trim(),
                    awayTeam: form.awayTeam.trim(),
                    match: form.matchLabel.trim(),
                    pick: form.pick.trim(),
                    market: form.market.trim(),
                    odd: form.odd ? Number(form.odd) : null,
                    confidence: form.confidence ? Number(form.confidence) : 0,
                    reason: form.reason.trim(),
                    stakeHint: form.stakeHint.trim(),
                    kickoffTime: '',
                    kickoffTimestamp: form.kickoffLabel.trim(),
                    status: 'pending',
                    createdAt: serverTimestamp()
                };
            }

            // STEP 1: Save match
            await addDoc(colRef, payload);
            console.log('[FootballAdmin] ✅ Match saved to Firestore');

            // STEP 2: 🔥 Create announcement with sendViaEmail flag
            if (shouldAnnounce) {
                console.log('[FootballAdmin] 📢 Creating announcement with EMAIL...');
                await addDoc(collection(db, 'announcements'), {
                    category: 'football',
                    subject: 'Football Update',
                    type: 'football',  // 🔥 Keep for backwards compatibility
                    title: `New Pick: ${matchTitle || 'New Football Pick'}`,
                    message: `A new ${activePlan} match has been posted.`,
                    body: `A new ${activePlan} match has been posted.`,
                    linkPath: '/football',
                    source: 'football-admin',
                    sendViaEmail: true,  // 🔥 THIS TRIGGERS EMAIL SENDING
                    createdAt: serverTimestamp()
                });
                console.log('[FootballAdmin] ✅ Announcement created with EMAIL');
            } else {
                console.log('[FootballAdmin] ℹ️ NO announcement created');
            }

            setForm(initialForm);
        } catch (err) {
            console.error('[FootballAdmin] ❌ Error:', err);
            setError('Could not save match.');
        } finally {
            setSaving(false);
            setSendAnnouncement(false);
            console.log('[FootballAdmin] === SAVE COMPLETED ===');
        }
    };

    // 3. MANUAL BROADCAST (Blue Button)
    const handleManualBroadcast = async () => {
        const confirmMsg = `📢 SEND BROADCAST TO ALL USERS?\n\nThis will email ALL users that new ${activePlan.toUpperCase()} matches are live.\n\nAre you sure?`;
        if (!window.confirm(confirmMsg)) return;
        setBroadcasting(true);
        try {
            let title = 'New Football Predictions';
            let body = 'Fresh football picks have been updated.';
            if (activePlan === 'freemium') {
                title = "New Free Tips Posted";
                body = "New Freemium football matches are now live on the dashboard.";
            } else if (activePlan === 'premium') {
                title = "Premium Singles Updated";
                body = "New High-Confidence Premium matches have been added. Check them out.";
            } else if (activePlan === 'hrhr') {
                title = "High Risk / High Reward";
                body = "New HR/HR accumulators and big odds are live.";
            }

            await addDoc(collection(db, 'announcements'), {
                category: 'football',
                subject: 'Football Update',
                type: 'football',
                title: title,
                message: body,
                body: body,
                linkPath: '/football',
                source: 'football-admin-broadcast',
                sendViaEmail: true,  // 🔥 THIS TRIGGERS EMAIL SENDING
                createdAt: serverTimestamp()
            });
            alert("✅ Broadcast Sent + Emails Queued!");
        } catch (err) {
            console.error("Broadcast failed:", err);
            alert("❌ Failed to send broadcast.");
        } finally {
            setBroadcasting(false);
        }
    };

    // 4. RESULTS BROADCAST (Green Button)
    const handleResultBroadcast = async () => {
        const customTitle = window.prompt("Enter Email Title:", "Results: 8/9 Wins Today! 🚀");
        if (!customTitle) return;
        const customBody = window.prompt("Enter Email Message:", "The Football AI performed perfectly today. Check the dashboard to see the green ticks.");
        if (!customBody) return;
        setBroadcasting(true);
        try {
            await addDoc(collection(db, 'announcements'), {
                category: 'football',
                subject: 'Football Results',
                type: 'football',
                title: customTitle,
                message: customBody,
                body: customBody,
                linkPath: '/football',
                source: 'football-admin-results',
                sendViaEmail: true,  // 🔥 THIS TRIGGERS EMAIL SENDING
                createdAt: serverTimestamp()
            });
            alert("✅ Results Announced + Emails Sent!");
        } catch (err) {
            console.error("Results Broadcast failed:", err);
            alert("❌ Failed to announce results.");
        } finally {
            setBroadcasting(false);
        }
    };

    const handleStatusChange = async (id, status) => {
        try {
            const { collectionName } = getCollectionAndDayField();
            const ref = doc(db, collectionName, id);
            await updateDoc(ref, { status, resolvedAt: serverTimestamp() });
        } catch (err) {
            console.error('Error updating status:', err);
        }
    };

    const handleDelete = async (id) => {
        try {
            const { collectionName } = getCollectionAndDayField();
            const ref = doc(db, collectionName, id);
            await deleteDoc(ref);
        } catch (err) {
            console.error('Error deleting match:', err);
        }
    };

    // === GATEKEEPER UI ===
    if (authLoading || checkingAdmin) {
        return <div className="fa__loading">Verifying clearance...</div>;
    }
    if (!isAdmin) {
        return (
            <div className="fa__locked">
                <Icon icon="solar:shield-cross-bold" className="fa__locked-icon" />
                <h3>Admin Access Required</h3>
            </div>
        );
    }

    return (
        <div className="fa">
            <div className="fa__inner">
                {/* Header */}
                <header className="fa__header">
                    <div className="fa__title-wrap">
                        <div className="fa__pill">
                            <Icon icon="solar:soccer-linear" className="fa__pill-icon" />
                            <span>Football Manager</span>
                        </div>
                        <h1 className="fa__title">Match Curation</h1>
                    </div>
                </header>

                {/* Plan tabs */}
                <div className="fa__plans">
                    <button
                        type="button"
                        className={`fa__plan-tab ${activePlan === 'freemium' ? 'fa__plan-tab--active' : ''}`}
                        onClick={() => handlePlanChange('freemium')}
                    >
                        <span className="fa__plan-label">Freemium</span>
                    </button>
                    <button
                        type="button"
                        className={`fa__plan-tab ${activePlan === 'premium' ? 'fa__plan-tab--active' : ''}`}
                        onClick={() => handlePlanChange('premium')}
                    >
                        <span className="fa__plan-label">Premium Singles</span>
                    </button>
                    <button
                        type="button"
                        className={`fa__plan-tab ${activePlan === 'hrhr' ? 'fa__plan-tab--active' : ''}`}
                        onClick={() => handlePlanChange('hrhr')}
                    >
                        <span className="fa__plan-label">HR/HR</span>
                    </button>
                </div>

                {/* Day toggle */}
                <div className="fa__plans" style={{ marginBottom: '1rem' }}>
                    <button
                        type="button"
                        className={`fa__plan-tab ${dayKey === 'history' ? 'fa__plan-tab--active' : ''}`}
                        onClick={() => handleDayChange('history')}
                    >
                        History
                    </button>
                    <button
                        type="button"
                        className={`fa__plan-tab ${dayKey === 'today' ? 'fa__plan-tab--active' : ''}`}
                        onClick={() => handleDayChange('today')}
                    >
                        {dayLabel('today')}
                    </button>
                    <button
                        type="button"
                        className={`fa__plan-tab ${dayKey === 'tomorrow' ? 'fa__plan-tab--active' : ''}`}
                        onClick={() => handleDayChange('tomorrow')}
                    >
                        {dayLabel('tomorrow')}
                    </button>
                </div>

                {error && (
                    <p style={{ fontSize: '0.78rem', color: '#fecaca', marginBottom: '0.6rem' }}>{error}</p>
                )}

                {/* Form + list layout */}
                <div className="fa__layout">
                    {/* Left: form */}
                    <section className="fa__panel">
                        <div className="fa__panel-header">
                            <h2 className="fa__panel-title">
                                Add {activePlan === 'freemium' ? 'Freemium' : 'Premium'} Ticket
                            </h2>
                        </div>
                        <form className="fa__form" onSubmit={handleSubmit}>
                            <div className="fa__form-grid">
                                <label className="fa__field">
                                    <span className="fa__field-label">League</span>
                                    <input
                                        type="text"
                                        name="league"
                                        value={form.league}
                                        onChange={handleChange}
                                        className="fa__input"
                                        placeholder="e.g. Premier League"
                                        required
                                    />
                                </label>
                                <label className="fa__field">
                                    <span className="fa__field-label">Match Label</span>
                                    <input
                                        type="text"
                                        name="matchLabel"
                                        value={form.matchLabel}
                                        onChange={handleChange}
                                        className="fa__input"
                                        placeholder="Man Utd vs Chelsea"
                                    />
                                </label>
                                <label className="fa__field">
                                    <span className="fa__field-label">Home Team</span>
                                    <input
                                        type="text"
                                        name="homeTeam"
                                        value={form.homeTeam}
                                        onChange={handleChange}
                                        className="fa__input"
                                        placeholder="Home Team"
                                    />
                                </label>
                                <label className="fa__field">
                                    <span className="fa__field-label">Away Team</span>
                                    <input
                                        type="text"
                                        name="awayTeam"
                                        value={form.awayTeam}
                                        onChange={handleChange}
                                        className="fa__input"
                                        placeholder="Away Team"
                                    />
                                </label>
                                <label className="fa__field">
                                    <span className="fa__field-label">Market</span>
                                    <input
                                        type="text"
                                        name="market"
                                        value={form.market}
                                        onChange={handleChange}
                                        className="fa__input"
                                        placeholder="BTTS / Multigoals"
                                        required={activePlan === 'freemium'}
                                    />
                                </label>
                                <label className="fa__field">
                                    <span className="fa__field-label">Pick / Selection</span>
                                    <input
                                        type="text"
                                        name="pick"
                                        value={form.pick}
                                        onChange={handleChange}
                                        className="fa__input"
                                        placeholder="Home Win / Over 2.5"
                                        required={activePlan !== 'freemium'}
                                    />
                                </label>
                                <label className="fa__field">
                                    <span className="fa__field-label">Odds</span>
                                    <input
                                        type="number"
                                        step="0.01"
                                        name="odd"
                                        value={form.odd}
                                        onChange={handleChange}
                                        className="fa__input"
                                        placeholder="1.60"
                                    />
                                </label>
                                <label className="fa__field">
                                    <span className="fa__field-label">Confidence %</span>
                                    <input
                                        type="number"
                                        min="0"
                                        max="100"
                                        name="confidence"
                                        value={form.confidence}
                                        onChange={handleChange}
                                        className="fa__input"
                                        placeholder="90"
                                    />
                                </label>
                                <label className="fa__field">
                                    <span className="fa__field-label">AI Edge</span>
                                    <input
                                        type="text"
                                        name="edge"
                                        value={form.edge}
                                        onChange={handleChange}
                                        className="fa__input"
                                        placeholder="+0.0% or @1.60"
                                    />
                                </label>
                                <label className="fa__field">
                                    <span className="fa__field-label">Kickoff Label</span>
                                    <input
                                        type="text"
                                        name="kickoffLabel"
                                        value={form.kickoffLabel}
                                        onChange={handleChange}
                                        className="fa__input"
                                        placeholder="today 14:00"
                                    />
                                </label>
                                <label className="fa__field">
                                    <span className="fa__field-label">Stake Hint</span>
                                    <input
                                        type="text"
                                        name="stakeHint"
                                        value={form.stakeHint}
                                        onChange={handleChange}
                                        className="fa__input"
                                        placeholder="1-2% of bankroll"
                                    />
                                </label>
                            </div>
                            <label className="fa__field fa__field--full">
                                <span className="fa__field-label">Reason / Notes</span>
                                <textarea
                                    name="reason"
                                    value={form.reason}
                                    onChange={handleChange}
                                    className="fa__textarea"
                                    rows={3}
                                    placeholder="Analysis notes..."
                                />
                            </label>
                            <div className="fa__form-actions">
                                <button
                                    type="submit"
                                    className="fa__btn fa__btn--primary"
                                    disabled={saving}
                                >
                                    <Icon icon="solar:check-circle-linear" className="fa__btn-icon" />
                                    <span>{saving ? 'Saving...' : 'Save Match'}</span>
                                </button>
                                {/* 🔥 ANNOUNCEMENT CHECKBOX */}
                                <label style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '0.55rem',
                                    marginLeft: '0.85rem',
                                    fontSize: '0.82rem',
                                    color: sendAnnouncement ? '#ef4444' : '#cbd5e1',
                                    fontWeight: sendAnnouncement ? 'bold' : 'normal',
                                    cursor: 'pointer',
                                    padding: sendAnnouncement ? '4px 8px' : '0',
                                    background: sendAnnouncement ? 'rgba(239, 68, 68, 0.1)' : 'transparent',
                                    borderRadius: '4px',
                                    border: sendAnnouncement ? '1px solid #ef4444' : 'none'
                                }}>
                                    <input
                                        type="checkbox"
                                        checked={sendAnnouncement}
                                        onChange={(e) => {
                                            const newValue = e.target.checked;
                                            console.log('[FootballAdmin] Checkbox changed to:', newValue);
                                            setSendAnnouncement(newValue);
                                        }}
                                        style={{ accentColor: sendAnnouncement ? '#ef4444' : undefined }}
                                    />
                                    <span>
                                        {sendAnnouncement
                                            ? '⚠️ WILL EMAIL ALL USERS!'
                                            : '📧 Email Announcement'}
                                    </span>
                                </label>
                            </div>
                        </form>
                    </section>

                    {/* Right: List */}
                    <section className="fa__panel fa__panel--list">
                        <div className="fa__panel-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem' }}>
                            <h2 className="fa__panel-title">
                                Active List ({dayLabel(dayKey)})
                            </h2>
                            <div style={{ display: 'flex', gap: '0.5rem' }}>
                                <button
                                    type="button"
                                    onClick={handleManualBroadcast}
                                    disabled={broadcasting || items.length === 0}
                                    style={{
                                        backgroundColor: '#4f46e5',
                                        color: '#fff',
                                        border: 'none',
                                        padding: '0.5rem 0.8rem',
                                        borderRadius: '6px',
                                        fontSize: '0.75rem',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '0.3rem',
                                        opacity: broadcasting || items.length === 0 ? 0.6 : 1
                                    }}
                                >
                                    <Icon icon="solar:megaphone-bold" width="16" />
                                    {broadcasting ? 'Sending...' : 'Broadcast All Picks'}
                                </button>
                                <button
                                    type="button"
                                    onClick={handleResultBroadcast}
                                    disabled={broadcasting}
                                    style={{
                                        backgroundColor: '#10b981',
                                        color: '#fff',
                                        border: 'none',
                                        padding: '0.5rem 0.8rem',
                                        borderRadius: '6px',
                                        fontSize: '0.75rem',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '0.3rem',
                                        opacity: broadcasting ? 0.6 : 1
                                    }}
                                >
                                    <Icon icon="solar:cup-star-bold" width="16" />
                                    Announce Results
                                </button>
                            </div>
                        </div>
                        {loading && (
                            <div className="fa__empty">
                                <Icon icon="solar:calendar-linear" className="fa__empty-icon" />
                                <p>Loading...</p>
                            </div>
                        )}
                        {!loading && items.length === 0 && (
                            <div className="fa__empty">
                                <Icon icon="solar:calendar-linear" className="fa__empty-icon" />
                                <p>No matches found.</p>
                            </div>
                        )}
                        {!loading && items.length > 0 && (
                            <ul className="fa__list">
                                {items.map((item) => (
                                    <li key={item.id} className="fa__row">
                                        <div className="fa__row-main">
                                            <div className="fa__row-match">
                                                <div className="fa__row-league">{item.league}</div>
                                                <div className="fa__row-teams">
                                                    {item.match || `${item.homeTeam || ''} vs ${item.awayTeam || ''}`}
                                                </div>
                                            </div>
                                            <div className="fa__row-meta">
                                                {item.market && (
                                                    <div className="fa__row-chip">
                                                        <Icon icon="solar:ball-linear" />
                                                        <span>{item.market}</span>
                                                    </div>
                                                )}
                                                {item.odd && (
                                                    <div className="fa__row-chip fa__row-chip--odds">
                                                        <span>{item.odd}</span>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        <div className="fa__row-side">
                                            <div className="fa__row-kickoff">
                                                <span>{item.kickoffLabel}</span>
                                            </div>
                                            <div className="fa__row-actions">
                                                <select
                                                    className="fa__status-select"
                                                    value={item.status || 'pending'}
                                                    onChange={(e) => handleStatusChange(item.id, e.target.value)}
                                                >
                                                    <option value="pending">Pending</option>
                                                    <option value="won">Won</option>
                                                    <option value="lost">Lost</option>
                                                </select>
                                                <button
                                                    type="button"
                                                    className="fa__delete-btn"
                                                    onClick={() => handleDelete(item.id)}
                                                >
                                                    <Icon icon="solar:trash-bin-trash-linear" />
                                                </button>
                                            </div>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </section>
                </div>
            </div>
        </div>
    );
};

export default FootballAdmin;